import { BookOpen, Download, FolderOpen, LogOut, Menu, MessageCircle, Plug, Settings, User, Video, X } from 'lucide-react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { BrowserRouter as Router, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { ToastProvider } from '@/hooks/useToast'
import { ROUTES } from '@/lib/routes'
import { cn } from '@/lib/utils'

import LoadingScreen from './components/LoadingScreen'
import { ToastContainer } from './components/Toast'
import { apiFetch } from './lib/api'
import { logger } from './lib/logger'
const ProjectsPage = lazy(() => import('./features/projects/ProjectsPage'))
const AssetsPage = lazy(() => import('./pages/AssetsPage'))
const RenderingsPage = lazy(() => import('./pages/RenderingsPage'))
const ImportPage = lazy(() => import('./pages/ImportPage'))
const ConnectionsPage = lazy(() => import('./pages/ConnectionsPage'))
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const PlayerPage = lazy(() => import('./pages/PlayerPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const HomePage = lazy(() => import('./pages/HomePage'))
const EditorPage = lazy(() => import('./pages/EditorPage'))

const ProtectedPlansPage = lazy(() => import('./pages/ProtectedPlansPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const playerRoutePaths = [
  '/player/:id',
  ROUTES.playerStockPreview,
  '/player-v2/:id',
  ROUTES.playerV2StockPreview,
  '/player-v1/:id',
  '/player-v1/stock-preview',
];

function RootPage() {
  const { user, subscription, loading, setSubscription } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (loading || !user) return
    // Allow /onboarding route without subscription
    if (location.pathname === '/onboarding') {
      return
    }
    if (!subscription) {
      const currentPath = location.pathname + location.search
      if (currentPath !== '/' && currentPath !== '') {
        navigate(`/plans?next=${encodeURIComponent(currentPath)}`, { replace: true })
      } else {
        navigate('/plans', { replace: true })
      }
    }
  }, [loading, user, subscription, navigate, location])

  if (loading) {
    return <LoadingScreen />
  }

  // Not authenticated - show 404 for protected routes, public home page for root
  if (!user) {
    // If trying to access a protected route (not root), show 404
    if (location.pathname !== '/' && location.pathname !== '') {
      return <NotFoundPage />
    }
    return <HomePage subscription={null} onSubscriptionChange={setSubscription} />
  }

  // Authenticated with subscription - show app with ProjectsPage
  if (user && subscription) {
    return <AppContent />
  }

  // Authenticated without subscription - allow onboarding route, otherwise redirect to plans
  if (location.pathname === '/onboarding') {
    return <OnboardingPage />
  }

  // Authenticated without subscription - will redirect to /plans (show loading meanwhile)
  return <LoadingScreen />
}

function AppContent() {
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, subscription, loading } = useAuth()
  const userMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const state = location.state as { backgroundLocation?: Location } | null
  const navigate = useNavigate()
  const isEditorRoute = location.pathname.startsWith('/editor/')

  // Check if current route is /onboarding (exempt from auth)
  const isOnboardingRoute = location.pathname === '/onboarding'

  const handleLogout = async () => {
    // Redirect immediately to prevent 404 flash
    window.location.href = '/'
    try {
      await apiFetch('/oauth/logout', { method: 'POST' })
    } catch (error) {
      logger.error('Logout failed:', error)
    }
  }

  const navItems = [
    { to: ROUTES.projects, label: 'Projects', icon: Video },
    { to: ROUTES.assets, label: 'Assets', icon: FolderOpen },
    { to: ROUTES.renderings, label: 'Renderings', icon: Download },
    { to: ROUTES.integrations, label: 'Integrations', icon: Plug },
    { to: ROUTES.chats, label: 'Chats', icon: MessageCircle },
  ]

  const userNavigation = [
    { name: 'Settings', href: ROUTES.settings, icon: Settings },
  ]

  const navClassName = (active: boolean) =>
    cn(
      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
      active
        ? 'bg-accent text-accent-foreground shadow-sm'
        : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground'
    )
  const NAV_ICON_CLASS = 'w-4 h-4 flex-shrink-0'
  const menuItemClass = 'flex items-center gap-2 px-3 py-2 text-sm transition-colors'

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  useEffect(() => {
    if (!loading && user && !subscription && !isOnboardingRoute && location.pathname !== ROUTES.plans) {
      navigate(ROUTES.plans, { replace: true })
    }
  }, [loading, user, subscription, location.pathname, navigate, isOnboardingRoute])

  // Show loading state while checking auth
  if (loading) {
    return <LoadingScreen />
  }

  // Show login page if not authenticated (except onboarding route)
  if (!user && !isOnboardingRoute) {
    // Redirect to login with next parameter to return to this page after auth
    const currentPath = location.pathname + location.search
    window.location.href = `/auth/login?next=${encodeURIComponent(currentPath)}`
    return <LoadingScreen />
  }

  if (isEditorRoute) {
    return (
      <div className="flex flex-col h-[100dvh] md:h-screen bg-gray-50">
        <div className="flex-1 overflow-hidden" data-scroll-container>
          <Suspense fallback={<LoadingScreen />}>
            <Routes location={state?.backgroundLocation || location}>
              <Route path="/editor/:id" element={<EditorPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>

        {/* Global Toast Container */}
        <ToastContainer />
      </div>
    )
  }

  return (
      <div className="flex h-[100dvh] md:h-screen bg-gray-50">
        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-gray-200 flex flex-col shadow-sm
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-4 border-b border-gray-200 h-16 flex items-center">
            <div className="flex items-center justify-between w-full lg:justify-center">
              <div className="hidden lg:flex items-center space-x-3">
                <img src="/logo.png" alt="Balboa Logo" className="h-6" loading="lazy" />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden ml-auto"
              >
                <X className="w-4 h-4 text-gray-500" />
              </Button>
            </div>
          </div>

          <nav className="flex-1 p-3 space-y-1">
            {navItems.map((item) => {
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                  className={({ isActive }) => {
                    // Check if current path is the item or one of its children
                    const isAssets = item.to === ROUTES.assets
                    const isCurrentPathActive = isAssets
                      ? location.pathname.startsWith(ROUTES.assets) ||
                        location.pathname === ROUTES.videos ||
                        location.pathname === ROUTES.uploads ||
                        location.pathname === ROUTES.stock ||
                        location.pathname === ROUTES.exportsRoot ||
                        location.pathname === ROUTES.audios
                      : isActive

                    return navClassName(isCurrentPathActive)
                  }}
                >
                  <item.icon className={NAV_ICON_CLASS} />
                  <span>{item.label}</span>
                </NavLink>
              )
            })}
          </nav>

          {/* Documentation Link */}
          <div className="p-3 border-t border-gray-200">
            <a
              href="https://docs.getbalboa.com"
              target="_blank"
              rel="noopener noreferrer"
              className={navClassName(false)}
            >
              <BookOpen className={NAV_ICON_CLASS} />
              <span>Docs</span>
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
          {/* Top Bar */}
          <div className="relative bg-white border-b border-gray-200 px-4 py-3 shadow-sm h-16 flex items-center justify-between">
                  {/* Left side: hamburger on mobile only */}
                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSidebarOpen(!sidebarOpen)}
                      className="lg:hidden"
                    >
                      <Menu className="w-4 h-4 text-gray-600" />
                    </Button>
                  </div>

            {/* Center: logo on mobile */}
            <div className="lg:hidden absolute left-1/2 -translate-x-1/2">
              <img src="/logo.png" alt="Balboa Logo" className="h-6" loading="lazy" />
            </div>
            
            {/* Right side: User Menu */}
            <div className="relative" ref={userMenuRef}>
              <DropdownMenu>
                <DropdownMenuTrigger
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center rounded-full hover:bg-accent p-1.5 transition-all duration-200"
                >
                  <div className="w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4 text-black" />
                  </div>
                </DropdownMenuTrigger>
                
                {userMenuOpen && (
                  <DropdownMenuContent
                    className="absolute right-0 mt-2 shadow-xl w-56 bg-white rounded-md"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <div className="px-3 py-2.5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                      <p className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</p>
                      <p className="text-xs text-gray-600">{user?.email || ''}</p>
                    </div>
                    <div className="py-1">
                      {userNavigation.map(item => (
                        <DropdownMenuItem 
                          key={item.name}
                          className={cn(menuItemClass, 'text-muted-foreground hover:bg-accent/20 hover:text-foreground')}
                          onClick={() => navigate(item.href)}
                        >
                          <item.icon className={NAV_ICON_CLASS} />
                          <span>{item.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                    <DropdownMenuSeparator />
                    <div className="py-1">
                      <DropdownMenuItem 
                        className={cn(menuItemClass, 'text-red-600 hover:bg-red-50')}
                        onClick={handleLogout}
                      >
                        <LogOut className={NAV_ICON_CLASS} />
                        <span>Log out</span>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
            </div>
          </div>

          {/* Page Content */}
          <div className="flex-1 overflow-auto" data-scroll-container>
            <Suspense fallback={<LoadingScreen />}>
              <Routes location={state?.backgroundLocation || location}>
                <Route path="/" element={<ProjectsPage />} />
                <Route path={ROUTES.assets} element={<AssetsPage />} />
                <Route path={ROUTES.videos} element={<AssetsPage />} />
                <Route path={ROUTES.images} element={<AssetsPage />} />
                <Route path={ROUTES.imageUploads} element={<AssetsPage />} />
                <Route path={ROUTES.imageStock} element={<AssetsPage />} />
                <Route path={ROUTES.renderings} element={<RenderingsPage />} />
                <Route path={ROUTES.importRoot} element={<ImportPage />} />
                <Route path={ROUTES.stockSearch} element={<ImportPage />} />
                {/* Short import aliases */}
                <Route path={ROUTES.import} element={<ImportPage />} />
                <Route path={ROUTES.importStock} element={<ImportPage />} />
                {/* Player routes render as full page when no backgroundLocation */}
                {playerRoutePaths.map((path) => (
                  <Route key={path} path={path} element={<PlayerPage />} />
                ))}
                <Route path={ROUTES.videos} element={<AssetsPage />} />
                <Route path={ROUTES.uploads} element={<AssetsPage />} />
                <Route path={ROUTES.stock} element={<AssetsPage />} />
                <Route path={ROUTES.exportsRoot} element={<AssetsPage />} />
                <Route path="/exports/:projectId" element={<AssetsPage />} />
                <Route path={ROUTES.audios} element={<AssetsPage />} />
                <Route path={ROUTES.chats} element={<ConnectionsPage />} />
                <Route path="/chats/:service" element={<ConnectionsPage />} />
                <Route path={ROUTES.integrations} element={<IntegrationsPage />} />
                <Route path={ROUTES.settings} element={<SettingsPage />} />
                <Route path="/editor/:id" element={<EditorPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            {state?.backgroundLocation && (
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {playerRoutePaths.map((path) => (
                    <Route key={path} path={path} element={<PlayerPage />} />
                  ))}
                </Routes>
              </Suspense>
            )}
          </div>
        </div>
        
        {/* Global Toast Container */}
        <ToastContainer />
      </div>
  )
}

function ProtectedPreviewPage() {
  const { user, loading } = useAuth()
  const location = useLocation()

  useEffect(() => {
    if (loading || user) return
    const currentPath = location.pathname + location.search
    window.location.href = `/auth/login?next=${encodeURIComponent(currentPath)}`
  }, [loading, user, location.pathname, location.search])

  if (loading) {
    return <LoadingScreen />
  }

  // User is authenticated - render PlayerPage
  // Backend will handle permission checking
  return <PlayerPage />
}

function App() {
  return (
    <Router basename="/">
      <AuthProvider>
        <ToastProvider>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Protected plans page for logged-in users without subscription */}
              <Route path="/plans" element={<ProtectedPlansPage />} />
              {/* Player-only preview routes (no app shell) - require auth */}
              <Route path="/preview/:id" element={<ProtectedPreviewPage />} />
              <Route path="/preview-v2/:id" element={<ProtectedPreviewPage />} />
              <Route path="/preview-v1/:id" element={<ProtectedPreviewPage />} />
              {/* Root and all app routes - RootPage decides what to show based on auth */}
              <Route path="/*" element={<RootPage />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
